import { useCallback, useState } from 'react';
import { Typography } from '@mui/material';

import { PostLogImportRecordsAPI } from 'api/postLogImportRecordsAPI';

import { OverflowTip } from 'components/UIComponents';
import Breadcrumbs from 'components/base/Breadcrumbs/Breadcrumbs';
import ListOfRecords from 'components/base/ListOfRecords';
import PageContainer from 'components/base/PageContainer/PageContainer';
import PageTitle from 'components/base/PageTitle/PageTitle';
import UploadPostLogsForm from 'components/forms/spots/UploadPostLogsForm';

import { useToast } from 'context/ToastContext';

import useWebSocket from 'hooks/useWebSocket';

import { ToastType } from 'constants/enums/PopupType';

import { IFilterParams } from 'interfaces/IFilterParams';
import { ITableColumn } from 'interfaces/ITableColumn';
import { IPostLogImportRecord } from 'interfaces/spots/IPostLogImportRecord';

import ExceptionsCell from './ExceptionsCell';
import { StyledLink, cellStyles, StyledStatusLabel } from './styledComponents';

const columns: ITableColumn[] = [
  { dataField: 'id', text: 'ID', style: cellStyles, headStyle: { width: '5%' } },
  { dataField: 'fileName', text: 'File Name', style: cellStyles, headStyle: { width: '16%' } },
  { dataField: 'status', text: 'Status', headStyle: { width: '10%' } },
  { dataField: 'rowsTotal', text: 'Total', style: cellStyles, headStyle: { width: '5%' } },
  { dataField: 'success', text: 'Success', style: cellStyles, headStyle: { width: '7%' } },
  { dataField: 'dnr', text: 'DNR', style: cellStyles, headStyle: { width: '5%' } },
  { dataField: 'exceptions', text: 'Excpt', style: cellStyles, headStyle: { width: '6%' } },
  { dataField: 'invalid', text: 'Invalid', style: cellStyles, headStyle: { width: '6%' } },
  { dataField: 'initiatedAt', text: 'Initiated at', style: cellStyles, headStyle: { width: '14%' } },
  { dataField: 'executedAt', text: 'Executed at', style: cellStyles, headStyle: { width: '14%' } },
  { dataField: 'createdBy', text: 'Initiated by', style: cellStyles, headStyle: { width: '8%' } },
  { dataField: 'download', text: '', headStyle: { width: '4%' } },
];

const ListOfPostLogImportRecords = () => {
  const { onToastOpen } = useToast();
  const [provider, setProvider] = useState(
    () => (params: IFilterParams) => PostLogImportRecordsAPI.getList(params)
  );

  const handleSocket = useCallback(
    (data: IPostLogImportRecord, type: string) => {
      if (type === 'update' && data.status === 'Success') {
        if ((data.spots_marked_dnr ?? 0) > 0) {
          onToastOpen(
            `post-log-dnr-${data.id}`,
            `${data.spots_marked_dnr} Spot records have been marked as DNR.`,
            ToastType.success
          );
        }
        if ((data.spots_marked_success ?? 0) > 0) {
          onToastOpen(
            `post-log-success-${data.id}`,
            `${data.spots_marked_success} Spot records have been marked as Success.`,
            ToastType.success
          );
        }
      }
      setProvider(() => (params: IFilterParams) => PostLogImportRecordsAPI.getList(params));
    },
    [onToastOpen]
  );

  useWebSocket('PostLogImportRecordsChannel', handleSocket);

  const dataFormatter = (item: IPostLogImportRecord) => {
    const hasIssues = (item.exceptions_count ?? 0) > 0 || (item.rows_invalid ?? 0) > 0;
    const canDownloadExceptions = item.status === 'Success' && hasIssues;
    return {
      id: <OverflowTip title={String(item.id)}>{item.id}</OverflowTip>,
      fileName: <OverflowTip title={item.file_name}>{item.file_name}</OverflowTip>,
      status: <StyledStatusLabel status={item.status}>{item.status}</StyledStatusLabel>,
      rowsTotal: item.rows_total ?? '',
      success: item.spots_marked_success ?? '',
      dnr: item.spots_marked_dnr ?? '',
      exceptions: item.exceptions_count ?? '',
      invalid: item.rows_invalid ?? '',
      initiatedAt: item.initiated_at ?? '',
      executedAt: item.executed_at ?? '',
      createdBy: item.created_by ?? '',
      download: (
        <ExceptionsCell
          recordId={item.id}
          fileName={item.file_name}
          hasIssues={canDownloadExceptions}
        />
      ),
    };
  };

  return (
    <PageContainer>
      <Breadcrumbs>
        <StyledLink to="/spots/legacy">Legacy Spots</StyledLink>
        <Typography>Post Log Import Monitor</Typography>
      </Breadcrumbs>
      <PageTitle>Post Log Import Monitor</PageTitle>
      <ListOfRecords
        tableProps={{
          name: 'postLogImportRecords',
          columns,
          rowsAsLinks: false,
          editAction: () => {},
          withActions: false,
          tableStyle: { display: 'table', tableLayout: 'fixed' },
        }}
        barActions={<UploadPostLogsForm />}
        dataFormatter={dataFormatter}
        dataProvider={provider}
        webSocketChannel="PostLogImportRecordsChannel"
        extraPageElementsHeight={41}
      />
    </PageContainer>
  );
};

export default ListOfPostLogImportRecords;
